import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { hasBackofficePermission } from "@/lib/rbac";
import { Search, CalendarDays, User, LayoutDashboard, Building2, BookOpen, Coins, LogOut, ShieldCheck, Users, Zap, BarChart3, Shield, Award, Star, Crown, CalendarRange, Share2, DollarSign, Globe, Target, Activity, ShieldAlert, FileCheck, MoreHorizontal, MapPin, Mail } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import api from "@/lib/api";
import { BUILD_VERSION } from "@/lib/version";

import NDASign from "@/pages/NDASign";

export default function Layout({ children }) {
  const { user, logout, checkAuth } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => {
    if (user?.role === "PLATFORM_ADMIN") {
      api.get("/admin/security-alerts/active-count")
        .then(res => setAlertCount(res.data.count || 0))
        .catch(() => {});
      const interval = setInterval(() => {
        api.get("/admin/security-alerts/active-count")
          .then(res => setAlertCount(res.data.count || 0))
          .catch(() => {});
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // NDA gate for coordinators
  if (user?.is_coordinator && !user?.nda_signed) {
    return <NDASign onSigned={checkAuth} />;
  }

  const playerNav = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Home" },
    { to: "/book-court", icon: Search, label: t("nav.explore") },
    { to: "/build-team", icon: Users, label: "My Team" },
    { to: "/events", icon: Zap, label: t("nav.events") },
    { to: "/bookings", icon: CalendarDays, label: t("nav.bookings") },
  ];
  const providerNav = [
    { to: "/provider", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/provider/venues", icon: Building2, label: "Venues" },
    { to: "/provider/events", icon: Zap, label: "My Events" },
    { to: "/provider/calendar", icon: CalendarRange, label: "Calendar" },
    { to: "/provider/bookings", icon: BookOpen, label: "Bookings" },
    { to: "/revenue-center?view=venue", icon: Coins, label: "Revenue" },
    { to: "/provider/analytics", icon: BarChart3, label: "Analytics" },
  ];
  const adminNav = [
    { to: "/admin", icon: ShieldCheck, label: "Overview" },
    { to: "/admin/kyc-review", icon: FileCheck, label: "KYC Review" },
    { to: "/admin/venue-verification", icon: MapPin, label: "SP / Venue Approval" },
    { to: "/admin/cfo", icon: DollarSign, label: "CFO" },
    { to: "/admin/coordinators", icon: Target, label: "Coordinators" },
    { to: "/admin/recruitment", icon: Building2, label: "Recruit" },
    { to: "/admin/trust", icon: Shield, label: "Trust" },
    { to: "/admin/users", icon: Users, label: "Users" },
    { to: "/admin/platform-logs", icon: Activity, label: "Logs" },
    { to: "/admin/alerts", icon: ShieldAlert, label: "Alerts", badge: alertCount },
    { to: "/admin/staff-users", icon: Users, label: "Staff Users" },
    { to: "/admin/rbac-roles", icon: Shield, label: "RBAC Roles" },
    { to: "/admin/checker-pools", icon: Users, label: "Checker Pools" },
    { to: "/admin/approval-queue", icon: FileCheck, label: "Approval Queue" },
    { to: "/admin/approval-history", icon: Activity, label: "Approval History" },
    { to: "/admin/email-changes", icon: Mail, label: "Email Changes" },
    { to: "/admin/staff-manual", icon: BookOpen, label: "Staff Manual" },
  ];

  const adminFilteredNav = user?.role === "PLATFORM_ADMIN" ? adminNav : adminNav.filter((item) => {
    const permissionMap = {
      "/admin": "dashboard.view",
      "/admin/kyc-review": "kyc.review.view",
      "/admin/venue-verification": "venue.approvals.view",
      "/admin/cfo": "finance.console.view",
      "/admin/coordinators": "coordinators.view",
      "/admin/recruitment": "recruitment.view",
      "/admin/providers": "providers.view",
      "/admin/users": "players.view",
      "/admin/platform-logs": "audit.logs.view",
      "/admin/alerts": "audit.logs.view",
      "/admin/staff-users": "staff.users.manage",
      "/admin/rbac-roles": "rbac.roles.manage",
      "/admin/checker-pools": "rbac.roles.manage",
      "/admin/approval-queue": "dashboard.view",
      "/admin/approval-history": "dashboard.view",
      "/admin/staff-manual": "dashboard.view",
    };
    return hasBackofficePermission(user, permissionMap[item.to] || "dashboard.view");
  }).filter(Boolean);
  const coordinatorNav = [
    { to: "/recruitment", icon: Target, label: "Pipeline" },
    { to: "/recruitment/prospects", icon: Building2, label: "Prospects" },
    { to: "/recruitment/kpis", icon: BarChart3, label: "KPIs" },
    { to: "/book-court", icon: Search, label: "Venues" },
  ];
  const condoNav = [
    { to: "/condo", icon: Building2, label: "Dashboard" },
    { to: "/condo/residents", icon: Users, label: "Residents" },
    { to: "/condo/rules", icon: Shield, label: "Rules" },
    { to: "/condo/reports", icon: BarChart3, label: "Reports" },
  ];
  const coachNav = [
    { to: "/sp", icon: LayoutDashboard, label: "SP Home" },
    { to: "/coach/sessions", icon: CalendarDays, label: t("nav.sessions") },
    { to: "/coach/event-requests", icon: Zap, label: "Event Requests" },
    { to: "/revenue-center?view=coach", icon: Coins, label: "Revenue" },
    { to: "/coach/ratings", icon: Star, label: "Ratings" },
  ];

  const nav = ["PLATFORM_ADMIN", "BACKOFFICE"].includes(user?.role) ? adminFilteredNav : user?.role === "CONDO_ADMIN" ? condoNav : user?.is_coordinator ? coordinatorNav : user?.is_coach ? coachNav : user?.role === "PROVIDER" ? providerNav : playerNav;
  const persona = user?.active_persona || "";
  const showAmbassador = persona === "VENUE_OWNER" || persona === "COACH_SP" || user?.role === "PROVIDER" || user?.is_coach;
  const commonMenuItems = [
    { to: "/play", icon: Coins, label: "$Play Wallet" },
    { to: "/sp/search", icon: Award, label: "Find Coach" },
    ...(showAmbassador ? [{ to: "/ambassador", icon: Share2, label: t("nav.ambassador") }] : []),
    { to: "/events/my", icon: Zap, label: t("nav.myEvents") },
  ];
  const mobilePrimaryNav = !user
    ? [
        { to: "/", icon: LayoutDashboard, label: "Home" },
        { to: "/search", icon: Search, label: t("nav.explore") },
        { to: "/events", icon: Zap, label: t("nav.events") },
      ]
    : ["PLATFORM_ADMIN", "BACKOFFICE"].includes(user.role)
      ? [adminFilteredNav[0], adminFilteredNav.find(item => item.to === "/admin/kyc-review") || adminFilteredNav[1], adminFilteredNav.find(item => item.to === "/admin/alerts")].filter(Boolean)
      : user.role === "CONDO_ADMIN"
        ? [condoNav[0], condoNav[1], condoNav[2]]
        : user.is_coordinator
          ? [coordinatorNav[0], coordinatorNav[1], coordinatorNav[2]]
          : user.is_coach
            ? [coachNav[0], coachNav[1], { to: "/coach/calendar", icon: CalendarRange, label: t("nav.calendar") }]
            : user.role === "PROVIDER"
              ? [providerNav[0], providerNav[1], providerNav[2]]
              : [
                  { to: "/dashboard", icon: LayoutDashboard, label: "Home" },
                  { to: "/book-court", icon: Search, label: t("nav.explore") },
                  { to: "/bookings/calendar", icon: CalendarRange, label: t("nav.calendar") },
                ];
  const mobileMenuItems = !user
    ? []
    : ["PLATFORM_ADMIN", "BACKOFFICE"].includes(user.role)
      ? [{ to: "/profile", icon: User, label: t("nav.profile") }, ...adminFilteredNav.filter((item) => item.to !== "/admin")]
      : user.role === "CONDO_ADMIN"
        ? [
            { to: "/profile", icon: User, label: t("nav.profile") },
            { to: "/condo/reports", icon: BarChart3, label: "Reports" },
          ]
        : user.is_coordinator
          ? [
              { to: "/profile", icon: User, label: t("nav.profile") },
              { to: "/book-court", icon: Search, label: "Venues" },
            ]
          : user.is_coach
            ? [
                { to: "/profile", icon: User, label: t("nav.profile") },
                { to: "/revenue-center?view=coach", icon: Coins, label: "Revenue" },
                { to: "/sp/search", icon: Search, label: "Find SP" },
                { to: "/coach/ratings", icon: Star, label: "Ratings" },
                { to: "/ambassador", icon: Share2, label: t("nav.ambassador") },
                { to: "/events/my", icon: Zap, label: t("nav.myEvents") },
              ]
            : user.role === "PROVIDER"
              ? [
                  { to: "/profile", icon: User, label: t("nav.profile") },
                  { to: "/revenue-center?view=venue", icon: Coins, label: "Revenue" },
                  { to: "/provider/bookings", icon: BookOpen, label: "Bookings" },
                  { to: "/provider/analytics", icon: BarChart3, label: "Analytics" },
                  { to: "/subscriptions/plans", icon: Crown, label: "Plan" },
                  ...commonMenuItems,
                ]
              : [
                  { to: "/profile", icon: User, label: t("nav.profile") },
                  { to: "/build-team", icon: Users, label: "My Teams" },
                  { to: "/events", icon: Zap, label: t("nav.events") },
                  { to: "/bookings", icon: CalendarDays, label: t("nav.bookings") },
                  ...commonMenuItems,
                ];
  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Nav */}
      <nav data-testid="desktop-nav" className="hidden md:flex sticky top-0 z-40 w-full border-b border-border/40 bg-white/95 backdrop-blur items-center justify-between px-8 h-16">
        <NavLink to="/" className="flex items-center gap-2">
          <img src="/logo.png" alt="SetPlay" className="h-9 w-9" />
          <span className="font-heading text-xl font-bold uppercase tracking-tight text-court-orange">SetPlay</span>
        </NavLink>
        <div className="flex items-center gap-1">
          {nav.map(n => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) =>
              `relative flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${isActive ? 'text-court-orange' : 'text-foreground/70 hover:text-foreground'}`
            }>
              <n.icon className="w-4 h-4" />{n.label}
              {n.badge > 0 && (
                <span data-testid="alert-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
                  {n.badge > 99 ? "99+" : n.badge}
                </span>
              )}
            </NavLink>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {/* Language Switcher */}
          <button onClick={() => setLang(lang === "en" ? "fil" : "en")}
            className="flex items-center gap-1 px-2 py-1.5 text-xs font-heading uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors rounded-full border border-border/50 hover:border-border"
            data-testid="lang-switcher">
            <Globe className="w-3.5 h-3.5" />
            {lang === "en" ? "FIL" : "EN"}
          </button>
          {user ? (
            <>
              <NotificationBell />
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-btn" variant="ghost" className="flex items-center gap-2 rounded-full">
                  {user.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full" /> : <User className="w-5 h-5" />}
                  <span className="text-sm font-medium max-w-[120px] truncate">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem className="text-xs text-muted-foreground">{user.email}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")}><User className="w-4 h-4 mr-2" />{t("nav.profile")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/play")}><Coins className="w-4 h-4 mr-2" />$Play Wallet</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/sp/search")}><Award className="w-4 h-4 mr-2" />Find Coach</DropdownMenuItem>
                {showAmbassador && <DropdownMenuItem onClick={() => navigate("/ambassador")}><Share2 className="w-4 h-4 mr-2" />{t("nav.ambassador")}</DropdownMenuItem>}
                <DropdownMenuItem onClick={() => navigate("/bookings/calendar")}><CalendarRange className="w-4 h-4 mr-2" />{t("nav.calendar")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/events/my")}><Zap className="w-4 h-4 mr-2" />{t("nav.myEvents")}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout}><LogOut className="w-4 h-4 mr-2" />{t("nav.logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          ) : (
            <Button data-testid="login-nav-btn" onClick={() => navigate("/login")} className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full px-6 font-heading uppercase tracking-wide">
              {t("nav.login")}
            </Button>
          )}
        </div>
      </nav>

      {/* Content */}
      <main className="pb-bottom-nav pt-mobile-safe md:pb-8 md:pt-0 overflow-x-hidden">{children}</main>

      {/* Build Version - bottom right, desktop only */}
      <div data-testid="build-version" className="hidden md:block fixed bottom-2 right-3 text-[10px] text-muted-foreground/40 font-mono select-none z-30">
        v{BUILD_VERSION}
      </div>

      {/* Mobile Bottom Nav */}
      <nav data-testid="mobile-nav" className="md:hidden fixed bottom-4 left-3 right-3 bg-night-asphalt/90 backdrop-blur-md text-white rounded-[28px] px-2 py-2 flex justify-between items-center z-50 shadow-2xl border border-white/10 bottom-nav-animate">
        {mobilePrimaryNav.map(n => (
          <NavLink key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className={({ isActive }) => `relative flex min-w-0 flex-1 flex-col items-center gap-0.5 px-2 py-1 ${isActive ? 'text-court-orange' : 'text-white/60'}`}>
            <n.icon className="w-5 h-5" /><span className="text-[10px] font-medium">{n.label}</span>
            {n.badge > 0 && (
              <span className="absolute -top-0.5 right-0.5 min-w-[14px] h-[14px] flex items-center justify-center rounded-full bg-red-500 text-white text-[8px] font-bold px-0.5">
                {n.badge > 9 ? "9+" : n.badge}
              </span>
            )}
          </NavLink>
        ))}
        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="flex min-w-0 flex-1 flex-col items-center gap-0.5 px-2 py-1 text-white/60" data-testid="mobile-more-menu-trigger">
                <MoreHorizontal className="w-5 h-5" /><span className="text-[10px] font-medium">More</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 mb-3" data-testid="mobile-more-menu">
              <DropdownMenuItem className="text-xs text-muted-foreground" data-testid="mobile-menu-email">{user.email}</DropdownMenuItem>
              <DropdownMenuSeparator />
              {mobileMenuItems.map(item => (
                <DropdownMenuItem key={item.to} onClick={() => navigate(item.to)} data-testid={`mobile-menu-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <item.icon className="w-4 h-4 mr-2" />{item.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="mobile-logout-btn"><LogOut className="w-4 h-4 mr-2" />{t("nav.logout")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <NavLink to="/login" data-testid="mobile-nav-login" className="flex min-w-0 flex-1 flex-col items-center gap-0.5 px-2 py-1 text-white/60">
            <User className="w-5 h-5" /><span className="text-[10px] font-medium">{t("nav.login")}</span>
          </NavLink>
        )}
      </nav>
    </div>
  );
}
